/**
 * Centralized Color Configuration
 * All colors used in the application are defined here
 *
 * Usage:
 * - Import colors directly: import { colors } from '@/config/colors'
 * - Use Tailwind classes: bg-primary, text-primary-dark, etc.
 */

export const colors = {
    // Primary Brand Colors
    primary: {
        DEFAULT: '#BEAF87',
        light: '#d4c9a8',
        dark: '#746649',
        darker: '#5a4f39',
        hover: '#a08550',
    },

    // Secondary/Accent Colors
    secondary: {
        DEFAULT: '#252526',
        light: '#3d3d3e',
        dark: '#1a1a1b',
    },

    // Status Colors
    status: {
        success: {
            bg: '#dcfce7',
            text: '#166534',
            border: '#86efac',
        },
        warning: {
            bg: '#fef3c7',
            text: '#92400e',
            border: '#fcd34d',
        },
        error: {
            bg: '#fee2e2',
            text: '#991b1b',
            border: '#fca5a5',
        },
        info: {
            bg: '#dbeafe',
            text: '#1e40af',
            border: '#93c5fd',
        },
    },

    // Contact Type Colors
    contactTypes: {
        buyer: {
            bg: '#dbeafe',
            text: '#1d4ed8',
        },
        seller: {
            bg: '#dcfce7',
            text: '#15803d',
        },
        renter: {
            bg: '#ffedd5',
            text: '#c2410c',
        },
        agent: {
            bg: '#f3e8ff',
            text: '#7c3aed',
        },
        serviceProfessional: {
            bg: '#fce7f3',
            text: '#be185d',
        },
    },

    // Agreement Status Colors
    agreementStatus: {
        draft: { bg: '#f3f4f6', text: '#374151' },
        sent: { bg: '#dbeafe', text: '#1d4ed8' },
        viewed: { bg: '#fef3c7', text: '#92400e' },
        signed: { bg: '#dcfce7', text: '#15803d' },
        cancelled: { bg: '#fee2e2', text: '#991b1b' },
        pending_approval: { bg: '#fef3c7', text: '#92400e' },
        approved: { bg: '#dcfce7', text: '#15803d' },
        sent_to_dotloop: { bg: '#dbeafe', text: '#1d4ed8' },
        dotloop_send_failed: { bg: '#fee2e2', text: '#991b1b' },
    },

    // UI Colors
    ui: {
        background: '#f9fafb',
        card: '#ffffff',
        border: '#e5e7eb',
        borderLight: '#f3f4f6',
        text: {
            primary: '#111827',
            secondary: '#6b7280',
            muted: '#9ca3af',
        },
    },
} as const;

// Tailwind-compatible color exports for tailwind.config.ts
export const tailwindColors = {
    primary: {
        DEFAULT: colors.primary.DEFAULT,
        light: colors.primary.light,
        dark: colors.primary.dark,
        darker: colors.primary.darker,
        hover: colors.primary.hover,
    },
    secondary: {
        DEFAULT: colors.secondary.DEFAULT,
        light: colors.secondary.light,
        dark: colors.secondary.dark,
    },
};

// CSS variable exports (optional, for CSS-in-JS)
export const cssVariables = {
    '--color-primary': colors.primary.DEFAULT,
    '--color-primary-light': colors.primary.light,
    '--color-primary-dark': colors.primary.dark,
    '--color-primary-hover': colors.primary.hover,
    '--color-secondary': colors.secondary.DEFAULT,
};

export default colors;
